import React, { useState } from 'react';
import { RegulatoryNewsArticle, toDate, generateNewsletter, generateCarousel } from '../../api/regulatoryNews';

interface Props {
  articles: RegulatoryNewsArticle[];
  criteriaLabel: string;
  onClose: () => void;
}

const formatDate = (d: Date): string =>
  d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

const CAROUSEL_SOFT_CAP = 10;
// Must match the hard limits in backend regulatoryNewsController.ts
const NEWSLETTER_MAX = 100;
const CAROUSEL_MAX = 30;

// Requests use responseType 'blob', so a JSON error body arrives as a Blob.
const serverErrorMessage = async (err: unknown): Promise<string | null> => {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (!(data instanceof Blob)) return null;
  try {
    const parsed = JSON.parse(await data.text());
    return typeof parsed?.error === 'string' && parsed.error !== 'Internal server error' ? parsed.error : null;
  } catch {
    return null;
  }
};

export const downloadBlob = (blob: Blob) => {
  const url = URL.createObjectURL(blob);
  const isMobile = window.innerWidth <= 768;
  if (isMobile) {
    window.location.href = url;
  } else {
    window.open(url, '_blank');
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

export const NewsletterModal: React.FC<Props> = ({ articles, criteriaLabel, onClose }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set(articles.map((a) => a.id)));
  const [generating, setGenerating] = useState<'newsletter' | 'carousel' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const orderedIds = () => articles.filter((a) => selected.has(a.id)).map((a) => a.id);

  const handleGenerateNewsletter = async () => {
    if (selected.size === 0) return;
    setGenerating('newsletter');
    setError(null);
    try {
      const blob = await generateNewsletter(orderedIds(), criteriaLabel);
      downloadBlob(blob);
      onClose();
    } catch (err) {
      setError((await serverErrorMessage(err)) || 'Could not generate the newsletter. Please try again.');
    } finally {
      setGenerating(null);
    }
  };

  const handleGenerateCarousel = async () => {
    if (selected.size === 0) return;
    setGenerating('carousel');
    setError(null);
    try {
      const blob = await generateCarousel(orderedIds(), criteriaLabel);
      downloadBlob(blob);
      onClose();
    } catch (err) {
      setError((await serverErrorMessage(err)) || 'Could not generate the LinkedIn carousel. Please try again.');
    } finally {
      setGenerating(null);
    }
  };

  const busy = generating !== null;
  const overNewsletter = selected.size > NEWSLETTER_MAX;
  const overCarousel = selected.size > CAROUSEL_MAX;
  const linkStyle: React.CSSProperties = {
    background: 'none', border: 'none', padding: 0, color: 'var(--c-primary)', cursor: 'pointer', fontSize: 13,
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: 16 }}>Newsletter — {criteriaLabel}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: 'var(--c-text-muted)', marginBottom: 12 }}>
            {selected.size} of {articles.length} article{articles.length === 1 ? '' : 's'} selected. Uncheck any you want to leave out.{' '}
            <button type="button" style={linkStyle} onClick={() => setSelected(new Set(articles.map((a) => a.id)))}>Select all</button>
            {' · '}
            <button type="button" style={linkStyle} onClick={() => setSelected(new Set())}>Clear</button>
          </p>
          {articles.length === 0 ? (
            <p style={{ fontSize: 14, color: 'var(--c-text-muted)' }}>No articles match the current filters.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '50vh', overflowY: 'auto' }}>
              {articles.map((a) => (
                <label
                  key={a.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    padding: '8px 10px',
                    border: '1px solid var(--c-border)',
                    borderRadius: 'var(--r)',
                    cursor: 'pointer',
                    background: selected.has(a.id) ? 'var(--c-primary-10)' : 'var(--c-surface)',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(a.id)}
                    onChange={() => toggle(a.id)}
                    style={{ marginTop: 3 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{a.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
                      {a.regulatorLabel} · {formatDate(toDate(a.publishedDate))}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
          {(overCarousel || overNewsletter) && (
            <p style={{ fontSize: 12, color: 'var(--c-error, #b00020)', marginTop: 10 }}>
              {overNewsletter
                ? `PDF supports up to ${NEWSLETTER_MAX} articles and the carousel up to ${CAROUSEL_MAX} — deselect ${selected.size - NEWSLETTER_MAX} for the PDF, or ${selected.size - CAROUSEL_MAX} for the carousel. Tip: filter by month or regulator first.`
                : `LinkedIn carousel supports up to ${CAROUSEL_MAX} articles — deselect ${selected.size - CAROUSEL_MAX} to enable it.`}
            </p>
          )}
          {!overCarousel && selected.size > CAROUSEL_SOFT_CAP && (
            <p style={{ fontSize: 12, color: 'var(--c-text-muted)', marginTop: 10 }}>
              Heads up: LinkedIn carousels perform best kept short — you've selected {selected.size}, which will still
              generate fine, just worth trimming down if this is for social.
            </p>
          )}
          {error && <p style={{ color: 'var(--c-error, #b00020)', fontSize: 13, marginTop: 10 }}>{error}</p>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-outline" onClick={handleGenerateCarousel} disabled={busy || selected.size === 0 || overCarousel}>
            {generating === 'carousel' ? 'Generating…' : `LinkedIn Carousel (${selected.size})`}
          </button>
          <button className="btn btn-accent" onClick={handleGenerateNewsletter} disabled={busy || selected.size === 0 || overNewsletter}>
            {generating === 'newsletter' ? 'Generating…' : `Generate PDF (${selected.size})`}
          </button>
        </div>
      </div>
    </div>
  );
};
